// Shared pieces of the public website (homepage, topic pages, company pages):
// header, footer, store buttons, social icons and the phone "Get the app" bar.
// Fill in the real store and social links below before launch.
import { useEffect, useState, type ReactElement } from 'react';
import { useCatalog } from '../lib/catalog';
import { Link, navigate } from '../lib/router';
import { Icon } from './Icon';
import { Logo, Wordmark } from './Logo';

export const STORE_LINKS = {
  play: 'https://play.google.com/store/apps/details?id=in.paper2pixel.kuchnaya',
  appStore: 'https://apps.apple.com/in/app/kuch-naya/id0000000000', // replace with the real App Store id
};

export const SOCIAL_LINKS: { name: string; href: string; icon: 'youtube' | 'instagram' | 'whatsapp' | 'facebook' | 'x' | 'linkedin' }[] = [
  { name: 'YouTube', href: 'https://www.youtube.com/@kuchnaya', icon: 'youtube' },
  { name: 'Instagram', href: 'https://www.instagram.com/kuchnaya', icon: 'instagram' },
  { name: 'WhatsApp channel', href: 'https://whatsapp.com/channel/kuchnaya', icon: 'whatsapp' },
  { name: 'Facebook', href: 'https://www.facebook.com/kuchnaya', icon: 'facebook' },
  { name: 'X', href: 'https://x.com/kuchnaya', icon: 'x' },
  { name: 'LinkedIn', href: 'https://www.linkedin.com/company/kuchnaya', icon: 'linkedin' },
];

export const COMPANY_PAGES: { slug: string; title: string; group: 'company' | 'legal' }[] = [
  { slug: 'about', title: 'About us', group: 'company' },
  { slug: 'creators', title: 'Become a creator', group: 'company' },
  { slug: 'careers', title: 'Careers', group: 'company' },
  { slug: 'contact', title: 'Contact us', group: 'company' },
  { slug: 'privacy', title: 'Privacy policy', group: 'legal' },
  { slug: 'terms', title: 'Terms of use', group: 'legal' },
  { slug: 'refunds', title: 'Refund and cancellation', group: 'legal' },
  { slug: 'grievance', title: 'Grievance officer', group: 'legal' },
];

/** Go to a homepage section from any public page ("/?s=plus"). */
export function goSection(id: string) {
  const h = window.location.hash;
  const onHome = h === '' || h === '#/' || h.startsWith('#/?') || h.startsWith('#/welcome');
  if (onHome && document.getElementById(id)) document.getElementById(id)!.scrollIntoView({ behavior: 'smooth', block: 'start' });
  else navigate('/welcome?s=' + id);   // /welcome works for signed-in people too ("/" sends them to Home)
}

// Simple one-colour brand glyphs for social links (drawn for this site, 24px grid).
const SOCIAL: Record<string, ReactElement> = {
  youtube: <><rect x="2.5" y="5.5" width="19" height="13" rx="4" /><path d="M10 9l5 3-5 3z" fill="currentColor" stroke="none" /></>,
  instagram: <><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.3" cy="6.7" r="1.1" fill="currentColor" stroke="none" /></>,
  whatsapp: <><path d="M4 20l1.3-4A8 8 0 1 1 8.4 19z" /><path d="M9 8.5c0 3.5 2.6 6.5 6.5 6.5l1-1.5-2-1-1 1c-1-.4-2.6-2-3-3l1-1-1-2z" fill="currentColor" stroke="none" /></>,
  facebook: <path d="M14 21v-8h3l.5-3.5H14V7.6c0-1 .3-1.7 1.8-1.7h1.8V2.8A24 24 0 0 0 15 2.7c-2.6 0-4.4 1.6-4.4 4.5v2.3H7.7V13h2.9v8z" fill="currentColor" stroke="none" />,
  x: <path d="M4 4l16 16M20 4L4 20" />,
  linkedin: <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M8 10v7M8 7v.01M12 17v-4a2 2 0 0 1 4 0v4M12 10v7" /></>,
};

export function SocialIcons() {
  return (
    <ul className="social-icons" aria-label="Follow Kuch Naya">
      {SOCIAL_LINKS.map((s) => (
        <li key={s.name}>
          <a href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.name} title={s.name} className="social-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{SOCIAL[s.icon]}</svg>
          </a>
        </li>
      ))}
    </ul>
  );
}

export function StoreButtons({ compact = false }: { compact?: boolean }) {
  return (
    <div className={'store-buttons' + (compact ? ' compact' : '')}>
      <a className="store-btn" href={STORE_LINKS.play} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play">
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 3.5v17l9-8.5z" fill="#34A853" /><path d="M5 3.5l9 8.5 3-2.8L7.2 3.6A1.6 1.6 0 0 0 5 3.5z" fill="#4285F4" /><path d="M5 20.5l9-8.5 3 2.8-9.8 5.6a1.6 1.6 0 0 1-2.2.1z" fill="#EA4335" /><path d="M17 9.2l2.8 1.6a1.4 1.4 0 0 1 0 2.4L17 14.8 14 12z" fill="#FBBC04" /></svg>
        <span><small>GET IT ON</small><b>Google Play</b></span>
      </a>
      <a className="store-btn" href={STORE_LINKS.appStore} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M10.5 18.5h3" /></svg>
        <span><small>Download on the</small><b>App Store</b></span>
      </a>
    </div>
  );
}

export function PublicHeader() {
  return (
    <header className="l-nav pub-nav">
      <div className="wrap l-nav-inner">
        <Logo to="/" size={34} />
        <nav className="l-links" aria-label="Main">
          <Link to="/home" className="lp-navlink">Watch videos</Link>
          <Link to="/topics" className="lp-navlink">Topics</Link>
          <button type="button" onClick={() => goSection('plus')}>Kuch Naya Plus</button>
          <Link to="/company/creators" className="lp-navlink">For creators</Link>
        </nav>
        <div className="l-cta">
          <Link to="/login" className="btn btn-ghost">Log in</Link>
          <button type="button" className="btn btn-primary" onClick={() => goSection('app')}><Icon name="download" size={16} /> Get the app</button>
        </div>
      </div>
    </header>
  );
}

export function PublicFooter() {
  const { topics: TOPICS } = useCatalog();
  const company = COMPANY_PAGES.filter((p) => p.group === 'company');
  const legal = COMPANY_PAGES.filter((p) => p.group === 'legal');
  return (
    <footer className="l-footer lp-footer">
      <div className="wrap lp-footer-grid">
        <div className="lp-footer-brand">
          <Wordmark size={30} />
          <span className="muted" lang="hi">देखो कुछ नया हर रोज़</span>
          <p className="muted small">Short shows for every home in Bharat: micro dramas, comedy, college life and smart how-tos.</p>
          <StoreButtons compact />
          <SocialIcons />
        </div>
        <nav aria-label="Topics" className="lp-footer-col">
          <b>Topics</b>
          {TOPICS.map((t) => <Link key={t.slug} to={`/topic/${t.slug}`} className="foot-link">{t.name}</Link>)}
          <Link to="/topics" className="foot-link">All topics</Link>
        </nav>
        <nav aria-label="Company" className="lp-footer-col">
          <b>Company</b>
          {company.map((p) => <Link key={p.slug} to={`/company/${p.slug}`} className="foot-link">{p.title}</Link>)}
          <button type="button" className="foot-link" onClick={() => goSection('faq')}>FAQs</button>
        </nav>
        <nav aria-label="Legal" className="lp-footer-col">
          <b>Legal</b>
          {legal.map((p) => <Link key={p.slug} to={`/company/${p.slug}`} className="foot-link">{p.title}</Link>)}
          <Link to="/login" className="foot-link">Log in</Link>
        </nav>
      </div>
      <div className="wrap lp-footer-bottom"><small>© 2026 Kuch Naya, a Paper2Pixel company. All rights reserved.</small></div>
    </footer>
  );
}

const BAR_KEY = 'kn-appbar-closed';

/** Slim bar on phones: "Kuch Naya app" + Get. Dismissible; remembered for this browser. */
export function AppBar() {
  const [closed, setClosed] = useState(() => { try { return window.localStorage.getItem(BAR_KEY) === '1'; } catch { return false; } });
  const [shown, setShown] = useState(false);
  useEffect(() => { const t = window.setTimeout(() => setShown(true), 1200); return () => window.clearTimeout(t); }, []);
  if (closed) return null;
  const close = () => { setClosed(true); try { window.localStorage.setItem(BAR_KEY, '1'); } catch { /* ignore */ } };
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const href = /iPhone|iPad|iPod/i.test(ua) ? STORE_LINKS.appStore : STORE_LINKS.play;
  return (
    <div className={'app-bar' + (shown ? ' shown' : '')} role="region" aria-label="Get the app">
      <span className="app-bar-icon" aria-hidden="true">क<i /></span>
      <span className="app-bar-text"><b>Kuch Naya app</b><small>Watch offline, get new-episode alerts</small></span>
      <a className="btn btn-sm btn-primary" href={href} target="_blank" rel="noopener noreferrer">Get</a>
      <button type="button" className="icon-btn" onClick={close} aria-label="Close"><Icon name="close" size={18} /></button>
    </div>
  );
}

/** Scroll to "?s=section" after landing on the homepage from another page. */
export function useSectionFromQuery(query: URLSearchParams) {
  const s = query.get('s');
  useEffect(() => {
    if (!s) return;
    const t = window.setTimeout(() => document.getElementById(s)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    return () => window.clearTimeout(t);
  }, [s]);
}
